import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Screen } from '@/components/ui/Screen';
import { TextField } from '@/components/ui/TextField';
import { AuthActionError, signUpWithPassword } from '@/lib/auth/actions';
import { useLocale } from '@/lib/i18n/LocaleProvider';

const brandLogo = require('../../../assets/images/brand-logo.png');

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignUpScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const submit = async () => {
    setFormError(null);
    if (displayName.trim().length < 2) {
      setFormError(t('auth.errors.shortName'));
      return;
    }
    if (!EMAIL_PATTERN.test(email.trim())) {
      setFormError(t('auth.errors.invalidEmail'));
      return;
    }
    if (password.length < 8) {
      setFormError(t('auth.errors.shortPassword'));
      return;
    }
    setSubmitting(true);
    try {
      const { needsEmailConfirmation } = await signUpWithPassword(
        email.trim(),
        password,
        displayName.trim(),
        locale,
      );
      if (needsEmailConfirmation) {
        setPendingEmail(email.trim());
      } else {
        router.replace('/(tabs)/profile');
      }
    } catch (error) {
      setFormError(
        error instanceof AuthActionError ? t(error.translationKey) : t('auth.errors.generic'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (pendingEmail) {
    return (
      <Screen>
        <BackHeader title={t('auth.signUp')} />
        <View className="px-4 pt-8">
          <Card className="items-center gap-3 px-6 py-8">
            <AppText variant="heading" className="text-center">
              {t('auth.verifyEmailTitle')}
            </AppText>
            <AppText variant="caption" className="text-center leading-5">
              {t('auth.verifyEmailBody', { email: pendingEmail })}
            </AppText>
            <Button
              label={t('auth.signIn')}
              compact
              className="mt-2"
              onPress={() => router.replace('/auth/sign-in')}
            />
          </Card>
        </View>
      </Screen>
    );
  }

  return (
    <Screen watermark={false}>
      <BackHeader />
      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 40, gap: 22 }}>
        <View className="items-center gap-3 pt-2">
          <Image
            source={brandLogo}
            style={{ height: 104, width: 104, borderRadius: 24 }}
            contentFit="cover"
          />
          <View className="items-center gap-1.5">
            <AppText variant="title" className="text-center">
              {t('auth.createTitle')}
            </AppText>
            <AppText variant="caption" className="max-w-[300px] text-center leading-5">
              {t('auth.createSubtitle')}
            </AppText>
          </View>
        </View>

        <SocialAuthButtons
          onError={setFormError}
          onSuccess={() => router.replace('/(tabs)/profile')}
        />

        <View className="flex-row items-center gap-3">
          <View className="h-px flex-1 bg-line" />
          <AppText variant="micro" className="text-muted">
            {t('auth.orWithEmail')}
          </AppText>
          <View className="h-px flex-1 bg-line" />
        </View>

        <TextField
          label={t('auth.displayName')}
          value={displayName}
          onChangeText={setDisplayName}
          autoComplete="name"
        />
        <TextField
          label={t('auth.email')}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          autoComplete="email"
          keyboardType="email-address"
          textContentType="emailAddress"
        />
        <TextField
          label={t('auth.password')}
          value={password}
          onChangeText={setPassword}
          secureTextEntry
          autoComplete="new-password"
          textContentType="newPassword"
        />
        {formError ? (
          <AppText variant="caption" className="text-loss">
            {formError}
          </AppText>
        ) : null}
        <Button label={t('auth.signUp')} loading={submitting} onPress={submit} />
        <Button
          label={t('auth.haveAccount')}
          variant="ghost"
          onPress={() => router.replace('/auth/sign-in')}
        />
      </ScrollView>
    </Screen>
  );
}
